import React, { useState } from "react";
import PinningModeMenu from "./ManagePage/ManualPinning/PinningModeMenu";
import ManualPinningContent from "./ManagePage/ManualPinning/ManualPinningContent";
import AutomaticPinningView from "./ManagePage/AutomaticPinningView";
import AllowedUrlsView from "./ManagePage/AllowedUrlsView";

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function ManageChannelModal({
    channel,
    language,
    currentUser,
    onClose,
}) {
    // "menu" | "manual" | "automatic" | "allowed_urls"
    const [view, setView] = useState("menu");
    const [activeLang, setActiveLang] = useState("en");

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-slideUp">
                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {/* Back button when not on menu */}
                        {view !== "menu" && (
                            <button
                                onClick={() => setView("menu")}
                                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                            </button>
                        )}
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center">
                                    <svg
                                        className="w-4 h-4 text-orange-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                </div>
                                <h2 className="text-lg font-bold text-gray-900">
                                    {view === "menu" &&
                                        (language === "zh"
                                            ? "管理頻道"
                                            : "Manage Channel")}
                                    {view === "manual" &&
                                        (language === "zh"
                                            ? "手動綁定"
                                            : "Manual Pinning")}
                                    {view === "automatic" &&
                                        (language === "zh"
                                            ? "自動綁定"
                                            : "Automatic Pinning")}
                                    {view === "allowed_urls" &&
                                        (language === "zh"
                                            ? "允許的來源網址"
                                            : "Allowed Origins")}
                                </h2>
                            </div>
                            <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-700">
                                    {channel.channel_name}
                                </span>
                                {view === "menu" &&
                                    (language === "zh"
                                        ? " · 選擇版本綁定策略"
                                        : " · Choose a pinning strategy")}
                                {view === "manual" &&
                                    (language === "zh"
                                        ? " · 手動選擇每個產品的版本"
                                        : " · Pin specific product versions per language")}
                                {view === "automatic" &&
                                    (language === "zh"
                                        ? " · 自動使用最新版本"
                                        : " · Automatically use latest versions")}
                                {view === "allowed_urls" &&
                                    (language === "zh"
                                        ? " · 管理 CORS 來源白名單"
                                        : " · Manage CORS origin whitelist")}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* ── Content ────────────────────────────────────────────── */}
                {view === "menu" && (
                    <PinningModeMenu
                        language={language}
                        onSelectMode={(mode) => setView(mode)}
                    />
                )}

                {view === "manual" && (
                    <ManualPinningContent
                        channel={channel}
                        language={language}
                        currentUser={currentUser}
                        activeLang={activeLang}
                        setActiveLang={setActiveLang}
                    />
                )}

                {view === "automatic" && (
                    <AutomaticPinningView
                        channel={channel}
                        language={language}
                        currentUser={currentUser}
                    />
                )}

                {view === "allowed_urls" && (
                    <AllowedUrlsView
                        channel={channel}
                        language={language}
                        currentUser={currentUser}
                    />
                )}

                {/* ── Bottom Done button (always visible) ────────────────── */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                        {language === "zh" ? "完成" : "Done"}
                    </button>
                </div>
            </div>
        </div>
    );
}
